import {serializeReviewAnalysisDate} from '../../../page/main/reviews-analysis/reviews-analysis-helper';
import {fetchAndDeserialize, serialize, serializeToURLParameters} from '../../../util/api-adapter';
import {FetchMethodEnum, fetchNoContent} from '../../../util/fetch';
import {PaginationDataType} from '../../../util/pagination-hook/pagination-hook-type';
import {objectToUrlParameters} from '../../../util/url';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../../api/api-const';
import {generateResponseSchema, PaginatedResponseType} from '../../api/api-type';

import {
    AspectsAnalysisRequestParametersType,
    AspectsAnalysisTableRequestParametersType,
    ExportAspectsReportOptionsType,
    reviewAspectRecordSchema,
    ReviewAspectRecordType,
    reviewAspectsToneDynamicsSchema,
    ReviewAspectsToneType,
    reviewsByAspectsDetailedResponseSchema,
    ReviewsByAspectsDetailedResponseType,
    reviewsCountByAspectsSchema,
    ReviewsCountByAspectsType,
    topAspectsItemSchema,
    TopAspectsItemType,
    TopAspectsQueryParametersType,
} from './review-aspects-type';

export function fetchReviewsAnalysisAspects(
    requestOptions: AspectsAnalysisTableRequestParametersType & PaginationDataType
): Promise<PaginatedResponseType<ReviewAspectRecordType>> {
    const {filterKey, sortQueryValue, page, pageSize, ...restRequestOptions} = requestOptions;

    return fetchAndDeserialize(
        `/v2/reviews-analytics/stats/aspects/?${objectToUrlParameters({
            ...serializeToURLParameters(restRequestOptions),
            ...serializeToURLParameters({page, pageSize}),
            filter_key: filterKey,
            sort: sortQueryValue,
        })}`,
        generateResponseSchema(reviewAspectRecordSchema)
    );
}

export function fetchReviewsAnalysisAspectsTone(
    requestOptions: AspectsAnalysisRequestParametersType
): Promise<ReviewAspectsToneType> {
    const {filterKey, ...restRequestOptions} = requestOptions;

    return fetchAndDeserialize(
        `/v2/reviews-analytics/stats/aspects-tonality-dynamics/?${objectToUrlParameters({
            ...serializeToURLParameters(restRequestOptions),
            filter_key: filterKey,
        })}`,
        reviewAspectsToneDynamicsSchema
    );
}

export function fetchReviewsByAspectsDetailed(
    requestOptions: AspectsAnalysisTableRequestParametersType & PaginationDataType
): Promise<ReviewsByAspectsDetailedResponseType> {
    const {filterKey, sortQueryValue, page, pageSize, ...restRequestOptions} = requestOptions;

    return fetchAndDeserialize(
        `/v2/reviews-analytics/stats/reviews-by-aspects-detailed/?${objectToUrlParameters({
            ...serializeToURLParameters(restRequestOptions),
            ...serializeToURLParameters({page, pageSize}),
            filter_key: filterKey,
            sort: sortQueryValue,
        })}`,
        reviewsByAspectsDetailedResponseSchema
    );
}

export function fetchReviewsCountByAspects(
    requestOptions: AspectsAnalysisRequestParametersType
): Promise<ReviewsCountByAspectsType> {
    const {filterKey, ...restRequestOptions} = requestOptions;

    return fetchAndDeserialize(
        `/v2/reviews-analytics/stats/reviews-by-aspects/?${objectToUrlParameters({
            ...serializeToURLParameters(restRequestOptions),
            filter_key: filterKey,
        })}`,
        reviewsCountByAspectsSchema
    );
}

export async function fetchTopAspects(
    mainFilterKey: string,
    filterParameters: TopAspectsQueryParametersType
): Promise<Array<TopAspectsItemType>> {
    const url = `/v2/reviews-analytics/stats/top-aspects/?${objectToUrlParameters({
        filter_key: mainFilterKey,
        ...serializeToURLParameters<TopAspectsQueryParametersType>(filterParameters),
    })}`;

    return fetchAndDeserialize<Array<TopAspectsItemType>>(url, topAspectsItemSchema.array());
}

export function requestAspectsReport(options: ExportAspectsReportOptionsType): Promise<void> {
    const url = `${rootApiUrl}/cp/reviews_analytics/download_aspect/`;
    const {comparedStartDate, comparedEndDate, startDate, endDate, ...restOptions} = options;

    return fetchNoContent(url, {
        method: FetchMethodEnum.post,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(
            serialize({
                ...restOptions,
                ...(startDate ? {startDate: serializeReviewAnalysisDate(startDate)} : {}),
                ...(endDate ? {endDate: serializeReviewAnalysisDate(endDate)} : {}),
                ...(comparedStartDate && comparedEndDate
                    ? {
                          comparedStartDate: serializeReviewAnalysisDate(comparedStartDate),
                          comparedEndDate: serializeReviewAnalysisDate(comparedEndDate),
                      }
                    : {}),
            })
        ),
    });
}
